import { z } from "@zod/mini";
import { generateTrackingId, getLocalStorage } from "./utilities";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Constants

export const SESSION_DETAILS_STORAGE_KEY = "dewey--analytics--sessionId";
export const SESSION_EXPIRATION_TIME = 30 * 60 * 1000; // 30 minutes

const SESSION_DETAILS_SCHEMA = z.object({
  id: z.string(),
  lastActiveAt: z.number(),
});
type SessionDetails = z.infer<typeof SESSION_DETAILS_SCHEMA>;

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Session ID

let sessionDetails: SessionDetails | undefined = undefined;

export function getSessionId(): string {
  // Load the session details, from memory or LocalStorage
  const $sessionDetails = sessionDetails ?? loadSessionDetails();

  let sessionId: string;
  if (isSessionExpired($sessionDetails)) {
    // If our session is expired (or we never had one), we need a new Session ID
    sessionId = generateTrackingId();
  } else {
    // If our session is still valid, we want to re-use the existing Session ID
    sessionId = $sessionDetails.id;
  }

  // Update the session details with the new Session ID and the current time as
  // our new lastActiveAt timestamp (and persist it to LocalStorage if available)
  sessionDetails = updateSessionDetails(sessionId);

  return sessionId;
}

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Saving / Loading

export function loadSessionDetails(): SessionDetails | undefined {
  const localStorage = getLocalStorage();
  if (localStorage) {
    const sessionDetailsString = localStorage.getItem(SESSION_DETAILS_STORAGE_KEY);
    return validateSessionDetails(sessionDetailsString);
  } else {
    return undefined;
  }
}

function updateSessionDetails(sessionId: string): SessionDetails {
  const sessionDetails = {
    id: sessionId,
    lastActiveAt: new Date().getTime(),
  };

  try {
    const sessionDetailsString = JSON.stringify(sessionDetails);
    getLocalStorage()?.setItem(SESSION_DETAILS_STORAGE_KEY, sessionDetailsString);
  } catch {
    // We weren't able to save the session details to LocalStorage, so we'll just
    // rely on the session details in memory
  }

  return sessionDetails;
}

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Session Validation

function validateSessionDetails(sessionDetailsString: string | null): SessionDetails | undefined {
  if (!sessionDetailsString) return undefined;

  const sessionDetailsJSON = JSON.parse(sessionDetailsString);
  const parsed = SESSION_DETAILS_SCHEMA.safeParse(sessionDetailsJSON);

  if (parsed.success) return parsed.data;
  else return undefined;
}

function isSessionExpired(sessionDetails?: SessionDetails): sessionDetails is undefined {
  if (!sessionDetails) return true;

  const { lastActiveAt } = sessionDetails;
  const timeElapsed = Date.now() - lastActiveAt;

  return timeElapsed > SESSION_EXPIRATION_TIME;
}
